import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import strings from "translate/language";
import {
  APPOINTMENT_HOURS,
  APPOINTMENT_MINUTES,
  APPOINTMENT_PERIODS,
  composeAppointmentTime,
  isAppointmentHourSelectable,
  isAppointmentMinuteSelectable,
  isAppointmentPeriodSelectable,
  isAppointmentTimeValuePast,
  parseAppointmentTimeParts,
} from "utils/appointmentDateTime";

const EMPTY_PARTS = { hour: "", minute: "", period: "AM" };

const AppointmentScheduleFields = ({
  dateId,
  hourId,
  selectedDate,
  onDateChange,
  appointmentTime,
  onTimeChange,
  dateError,
  timeError,
  minDate = new Date(),
  maxDate = moment().add(30, "days").toDate(),
  dateLabel = strings.AppointmentDate,
  timeLabel = strings.AppointmentTime,
  datePlaceholder = strings.selectAppointmentDate,
  showTimeHint = true,
  required = true,
}) => {
  const [dateOpen, setDateOpen] = useState(false);
  const [timeDraft, setTimeDraft] = useState(() =>
    parseAppointmentTimeParts(appointmentTime),
  );
  const dateRootRef = useRef(null);

  useEffect(() => {
    if (appointmentTime) {
      setTimeDraft(parseAppointmentTimeParts(appointmentTime));
      return;
    }
    setTimeDraft((prev) => {
      const composed = composeAppointmentTime(
        prev.hour,
        prev.minute,
        prev.period,
      );
      return composed ? EMPTY_PARTS : prev;
    });
  }, [appointmentTime]);

  // Clear time if it is already past for the selected day (e.g. switched to today).
  useEffect(() => {
    if (!selectedDate || !appointmentTime) return;
    if (isAppointmentTimeValuePast(selectedDate, appointmentTime)) {
      onTimeChange("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid loops from inline onTimeChange
  }, [selectedDate, appointmentTime]);

  useEffect(() => {
    if (!dateOpen) {
      return undefined;
    }
    const onPointerDown = (event) => {
      if (!dateRootRef.current?.contains(event.target)) {
        setDateOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setDateOpen(false);
      }
    };
    document.addEventListener("click", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [dateOpen]);

  const handleTimePart = (part, value) => {
    setDateOpen(false);
    const nextDraft = { ...timeDraft, [part]: value };
    if (part === "period" && !isAppointmentPeriodSelectable(selectedDate, value)) {
      return;
    }
    if (
      part === "hour" &&
      value &&
      !isAppointmentHourSelectable(selectedDate, value, nextDraft.period)
    ) {
      return;
    }
    if (
      part === "minute" &&
      value !== "" &&
      !isAppointmentMinuteSelectable(
        selectedDate,
        nextDraft.hour,
        value,
        nextDraft.period,
      )
    ) {
      return;
    }

    if (
      (part === "hour" || part === "period") &&
      nextDraft.hour &&
      nextDraft.minute !== "" &&
      !isAppointmentMinuteSelectable(
        selectedDate,
        nextDraft.hour,
        nextDraft.minute,
        nextDraft.period,
      )
    ) {
      nextDraft.minute = "";
    }

    setTimeDraft(nextDraft);
    onTimeChange(
      composeAppointmentTime(nextDraft.hour, nextDraft.minute, nextDraft.period),
    );
  };

  return (
    <div className="justo-appointment-form__schedule">
      <div className="justo-appointment__field">
        <label className="justo-appointment__label" htmlFor={dateId}>
          {dateLabel}
          {required ? <span className="required-field" /> : null}
        </label>
        <div className="justo-appointment-form__date" ref={dateRootRef}>
          <div className="justo-appointment-form__date-control">
            <button
              id={dateId}
              type="button"
              className={`justo-appointment__input justo-appointment-form__date-trigger${
                selectedDate ? "" : " is-empty"
              }`}
              aria-expanded={dateOpen}
              aria-haspopup="dialog"
              onClick={() => setDateOpen((open) => !open)}
            >
              {selectedDate
                ? moment(selectedDate).format("DD/MM/YYYY")
                : datePlaceholder}
            </button>
            <i
              className="fa fa-calendar justo-appointment-form__date-icon"
              aria-hidden="true"
            />
          </div>
          {dateOpen ? (
            <div
              className="justo-appointment-form__date-panel"
              role="dialog"
              aria-label={dateLabel}
            >
              <DatePicker
                selected={selectedDate}
                onChange={(date) => {
                  onDateChange(date);
                  setDateOpen(false);
                }}
                minDate={minDate}
                maxDate={maxDate}
                inline
                calendarClassName="justo-datepicker"
                showDisabledMonthNavigation
                formatWeekDay={(label) => String(label).slice(0, 2)}
                calendarStartDay={1}
              />
            </div>
          ) : null}
        </div>
        {dateError ? (
          <span className="justo-appointment__error">{dateError}</span>
        ) : null}
      </div>
      <div className="justo-appointment__field justo-appointment-form__time-col">
        <label className="justo-appointment__label" htmlFor={hourId}>
          {timeLabel}
          {required ? <span className="required-field" /> : null}
        </label>
        <div className="justo-appointment-form__time">
          <select
            id={hourId}
            className="justo-appointment__input"
            value={timeDraft.hour}
            onChange={(event) => handleTimePart("hour", event.target.value)}
            aria-label="Hour"
          >
            <option value="">HH</option>
            {APPOINTMENT_HOURS.map((hour) => {
              const disabled = !isAppointmentHourSelectable(
                selectedDate,
                hour,
                timeDraft.period,
              );
              return (
                <option key={hour} value={hour} disabled={disabled}>
                  {hour}
                </option>
              );
            })}
          </select>
          <span className="justo-appointment-form__time-sep" aria-hidden="true">
            :
          </span>
          <select
            className="justo-appointment__input"
            value={timeDraft.minute}
            onChange={(event) => handleTimePart("minute", event.target.value)}
            aria-label="Minute"
          >
            <option value="">MM</option>
            {APPOINTMENT_MINUTES.map((minute) => {
              const disabled = !isAppointmentMinuteSelectable(
                selectedDate,
                timeDraft.hour,
                minute,
                timeDraft.period,
              );
              return (
                <option key={minute} value={minute} disabled={disabled}>
                  {minute}
                </option>
              );
            })}
          </select>
          <div
            className="justo-appointment-form__period"
            role="group"
            aria-label="AM or PM"
          >
            {APPOINTMENT_PERIODS.map((period) => {
              const disabled = !isAppointmentPeriodSelectable(
                selectedDate,
                period,
              );
              return (
                <button
                  key={period}
                  type="button"
                  disabled={disabled}
                  className={`justo-appointment-form__period-btn${
                    timeDraft.period === period ? " is-active" : ""
                  }`}
                  onClick={() => handleTimePart("period", period)}
                >
                  {period}
                </button>
              );
            })}
          </div>
        </div>
        {showTimeHint ? (
          <p className="justo-appointment-form__hint">
            {strings.AppointmentTimeDetail}
          </p>
        ) : null}
        {timeError ? (
          <span className="justo-appointment__error">{timeError}</span>
        ) : null}
      </div>
    </div>
  );
};

export default AppointmentScheduleFields;
